/**
 * Offline Support Module
 *
 * Provides offline mutation queueing, connectivity detection, and conflict resolution
 * for the Ref Mode feature and Walk-In Registration. Uses localStorage for persistence
 * and navigator.onLine for connectivity detection.
 *
 * Requirements: 6.1, 6.2, 6.6 (Ref Mode)
 * Requirements: 10.3, 10.5 (Walk-In Registration)
 */

// =============================================================================
// Types
// =============================================================================

/**
 * Mutation types supported by the offline queue
 * - Ref Mode: recordBattle, undoLastBattle, completeMatch
 * - Walk-In Registration: registerWalkIn, createWalkInProfile
 */
export type MutationType =
  | 'recordBattle'
  | 'undoLastBattle'
  | 'completeMatch'
  | 'registerWalkIn'
  | 'createWalkInProfile';

/**
 * Base interface for queued mutations
 */
export interface QueuedMutation {
  id: string;
  type: MutationType;
  /**
   * Entity ID for the mutation context:
   * - For ref mode mutations: matchId
   * - For walk-in mutations: tournamentId
   * Either entityId or matchId must be provided.
   */
  entityId: string;
  /**
   * @deprecated Use entityId instead. Kept for backward compatibility with existing queued mutations.
   * When provided without entityId, will be used as entityId.
   */
  matchId?: string;
  payload: Record<string, unknown>;
  timestamp: number;
  retryCount: number;
}

/**
 * Input type for queueMutation - allows either entityId or matchId
 */
export type QueueMutationInput = Omit<QueuedMutation, 'id' | 'timestamp' | 'retryCount' | 'entityId'> & {
  entityId?: string;
  matchId?: string;
};

export interface OfflineState {
  isOnline: boolean;
  pendingMutations: QueuedMutation[];
  lastSyncTime: number | null;
}

/**
 * Conflict types that can occur during sync
 */
export type ConflictType =
  | 'SERVER_WINS'
  | 'ALREADY_REGISTERED'  // Walk-in: player already registered for tournament
  | 'DUPLICATE_PROFILE';  // Walk-in: profile with same name already exists

export interface SyncConflict {
  /**
   * Entity ID for the conflict context (matchId or tournamentId)
   */
  entityId: string;
  /**
   * @deprecated Use entityId instead
   */
  matchId?: string;
  localState: unknown;
  serverState: unknown;
  resolution: ConflictType;
}

type ConnectivityCallback = (isOnline: boolean) => void;
type SyncCompleteCallback = () => void;
type SyncConflictCallback = (conflict: SyncConflict) => void;
type PendingCountCallback = (count: number) => void;

// =============================================================================
// Constants
// =============================================================================

const STORAGE_KEY = 'bbx_offline_mutations';
const WALKIN_STORAGE_KEY = 'bbx_offline_walkin_mutations';
const MAX_RETRY_COUNT = 3;
const RETRY_DELAY_MS = 1000;
const MAX_MUTATION_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Mutation types that belong to walk-in registration
 */
const WALKIN_MUTATION_TYPES: MutationType[] = ['registerWalkIn', 'createWalkInProfile'];

/**
 * Check if a mutation type is a walk-in mutation
 */
function isWalkInMutation(type: MutationType): boolean {
  return WALKIN_MUTATION_TYPES.includes(type);
}

// =============================================================================
// Offline Manager Class
// =============================================================================

/**
 * OfflineManager handles offline mutation queueing and sync
 *
 * Key features:
 * - Queues mutations when offline
 * - Persists queue to localStorage (separate keys for ref mode and walk-in)
 * - Automatically syncs when back online
 * - Server-wins conflict resolution
 * - Walk-in specific conflict handling (already registered = success)
 *
 * Requirements: 6.1, 6.2, 6.6 (Ref Mode)
 * Requirements: 10.3, 10.5 (Walk-In Registration)
 */
class OfflineManager {
  private isOnline: boolean;
  private pendingMutations: QueuedMutation[] = [];
  private walkInMutations: QueuedMutation[] = [];
  private connectivityCallbacks: Set<ConnectivityCallback> = new Set();
  private syncCompleteCallbacks: Set<SyncCompleteCallback> = new Set();
  private syncConflictCallbacks: Set<SyncConflictCallback> = new Set();
  private pendingCountCallbacks: Set<PendingCountCallback> = new Set();
  private isSyncing = false;

  constructor() {
    this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.loadFromStorage();
    this.setupEventListeners();
  }

  // ===========================================================================
  // Public API
  // ===========================================================================

  /**
   * Check if the device is currently online
   */
  getIsOnline(): boolean {
    return this.isOnline;
  }

  /**
   * Get the number of pending mutations (all types)
   */
  getPendingMutationCount(): number {
    return this.pendingMutations.length + this.walkInMutations.length;
  }

  /**
   * Get the number of pending walk-in mutations only
   */
  getPendingWalkInMutationCount(): number {
    return this.walkInMutations.length;
  }

  /**
   * Get all pending mutations (both ref mode and walk-in)
   */
  getPendingMutations(): QueuedMutation[] {
    return [...this.pendingMutations, ...this.walkInMutations];
  }

  /**
   * Get pending walk-in mutations only
   */
  getPendingWalkInMutations(): QueuedMutation[] {
    return [...this.walkInMutations];
  }

  /**
   * Add a mutation to the offline queue
   * Automatically routes to the correct queue based on mutation type
   * Accepts either entityId or matchId for backward compatibility
   */
  queueMutation(mutation: QueueMutationInput): void {
    // Resolve entityId from either entityId or matchId
    const entityId = mutation.entityId || mutation.matchId || '';
    
    const queuedMutation: QueuedMutation = {
      ...mutation,
      id: generateId(),
      timestamp: Date.now(),
      retryCount: 0,
      entityId,
      // Keep matchId for backward compatibility with existing code
      matchId: mutation.matchId,
    };

    if (isWalkInMutation(mutation.type)) {
      this.walkInMutations.push(queuedMutation);
      this.saveWalkInToStorage();
    } else {
      this.pendingMutations.push(queuedMutation);
      this.saveToStorage();
    }
    this.notifyPendingCountChange();
  }

  /**
   * Remove a mutation from the queue (after successful sync)
   */
  removeMutation(id: string): void {
    const initialRefCount = this.pendingMutations.length;
    const initialWalkInCount = this.walkInMutations.length;

    this.pendingMutations = this.pendingMutations.filter((m) => m.id !== id);
    this.walkInMutations = this.walkInMutations.filter((m) => m.id !== id);

    if (this.pendingMutations.length !== initialRefCount) {
      this.saveToStorage();
    }
    if (this.walkInMutations.length !== initialWalkInCount) {
      this.saveWalkInToStorage();
    }
    this.notifyPendingCountChange();
  }

  /**
   * Clear all pending mutations for a specific entity (match or tournament)
   */
  clearMutationsForEntity(entityId: string): void {
    const initialRefCount = this.pendingMutations.length;
    const initialWalkInCount = this.walkInMutations.length;

    this.pendingMutations = this.pendingMutations.filter(
      (m) => m.entityId !== entityId && m.matchId !== entityId
    );
    this.walkInMutations = this.walkInMutations.filter(
      (m) => m.entityId !== entityId
    );

    if (this.pendingMutations.length !== initialRefCount) {
      this.saveToStorage();
    }
    if (this.walkInMutations.length !== initialWalkInCount) {
      this.saveWalkInToStorage();
    }
    this.notifyPendingCountChange();
  }

  /**
   * Clear all pending mutations for a specific match
   * @deprecated Use clearMutationsForEntity instead
   */
  clearMutationsForMatch(matchId: string): void {
    this.clearMutationsForEntity(matchId);
  }

  /**
   * Clear all pending mutations (manual cleanup)
   */
  clearAllMutations(): void {
    this.pendingMutations = [];
    this.walkInMutations = [];
    this.saveToStorage();
    this.saveWalkInToStorage();
    this.notifyPendingCountChange();
  }

  /**
   * Subscribe to connectivity changes
   */
  onConnectivityChange(callback: ConnectivityCallback): () => void {
    this.connectivityCallbacks.add(callback);
    // Immediately notify of current state
    callback(this.isOnline);
    return () => {
      this.connectivityCallbacks.delete(callback);
    };
  }

  /**
   * Subscribe to sync completion events
   */
  onSyncComplete(callback: SyncCompleteCallback): () => void {
    this.syncCompleteCallbacks.add(callback);
    return () => {
      this.syncCompleteCallbacks.delete(callback);
    };
  }

  /**
   * Subscribe to sync conflict events
   */
  onSyncConflict(callback: SyncConflictCallback): () => void {
    this.syncConflictCallbacks.add(callback);
    return () => {
      this.syncConflictCallbacks.delete(callback);
    };
  }

  /**
   * Subscribe to pending mutation count changes
   */
  onPendingCountChange(callback: PendingCountCallback): () => void {
    this.pendingCountCallbacks.add(callback);
    // Immediately notify of current count
    callback(this.pendingMutations.length);
    return () => {
      this.pendingCountCallbacks.delete(callback);
    };
  }

  /**
   * Manually trigger a sync attempt
   * @param executeMutation Function to execute each mutation against the server
   */
  async sync(
    executeMutation: (mutation: QueuedMutation) => Promise<{
      success: boolean;
      serverState?: unknown;
      errorCode?: string;
    }>
  ): Promise<void> {
    if (this.isSyncing || !this.isOnline) {
      return;
    }

    const totalMutations = this.pendingMutations.length + this.walkInMutations.length;
    if (totalMutations === 0) {
      return;
    }

    this.isSyncing = true;

    try {
      // Process ref mode mutations first (FIFO)
      await this.processMutationQueue(
        [...this.pendingMutations],
        executeMutation,
        false
      );

      // Process walk-in mutations (FIFO)
      await this.processMutationQueue(
        [...this.walkInMutations],
        executeMutation,
        true
      );

      this.notifySyncComplete();
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Process a queue of mutations with appropriate conflict resolution.
   *
   * FIFO per entity is a hard guarantee, not best-effort (pre-beta audit
   * P1-4): if a mutation for a given entityId (matchId) fails and stays
   * queued for retry, we must NOT go on to execute a *later* mutation for
   * that same entity in this pass. Concretely: a `recordBattle` that fails
   * transiently, followed in the queue by a `completeMatch` for the same
   * match, must not let the completeMatch run first — the match would
   * complete with a stale score, and the battle recorded later (if the
   * server now rejects late battles on a COMPLETED match — see the
   * complete-knockout-match/completeMatch hardening — would then be an
   * error, or worse silently drop, instead of that being caught here at
   * the point it's actually preventable) so any mutation sharing an
   * `entityId` with an already-failed one in this pass is skipped (left
   * queued, untouched) until the next sync pass, once the earlier one has
   * either succeeded or exhausted its retries.
   */
  private async processMutationQueue(
    mutations: QueuedMutation[],
    executeMutation: (mutation: QueuedMutation) => Promise<{
      success: boolean;
      serverState?: unknown;
      errorCode?: string;
    }>,
    isWalkIn: boolean
  ): Promise<void> {
    const blockedEntityIds = new Set<string>();

    for (const mutation of mutations) {
      if (blockedEntityIds.has(mutation.entityId)) {
        // An earlier mutation for this same entity failed in this pass —
        // don't let this one jump ahead of it. Leave it queued untouched;
        // it'll be tried again next sync pass, in order, once the earlier
        // mutation has resolved.
        continue;
      }

      try {
        const result = await executeMutation(mutation);

        if (result.success) {
          this.removeMutation(mutation.id);
        } else {
          // Handle walk-in specific conflict resolution
          // Per design doc: "Conflicts (player already registered) resolved with server-wins strategy"
          // This means if the server says "already registered", we treat it as success
          if (isWalkIn && this.isWalkInConflictResolvable(result.errorCode)) {
            // Server-wins: the player is already registered, which is the desired outcome
            this.notifySyncConflict({
              entityId: mutation.entityId,
              matchId: mutation.matchId,
              localState: mutation.payload,
              serverState: result.serverState,
              resolution: this.getWalkInConflictResolution(result.errorCode),
            });
            // Remove the mutation since the desired state exists on server
            this.removeMutation(mutation.id);
          } else {
            // Standard retry logic
            mutation.retryCount += 1;
            blockedEntityIds.add(mutation.entityId);

            if (mutation.retryCount >= MAX_RETRY_COUNT) {
              // Max retries reached - notify of conflict and remove
              this.notifySyncConflict({
                entityId: mutation.entityId,
                matchId: mutation.matchId,
                localState: mutation.payload,
                serverState: result.serverState,
                resolution: 'SERVER_WINS',
              });
              this.removeMutation(mutation.id);
            } else {
              // Wait before next retry
              await delay(RETRY_DELAY_MS * mutation.retryCount);
            }
          }
        }
      } catch (error) {
        console.error('Failed to sync mutation:', error);
        mutation.retryCount += 1;
        blockedEntityIds.add(mutation.entityId);

        if (mutation.retryCount >= MAX_RETRY_COUNT) {
          // Max retries reached on a thrown error (e.g. network failure) —
          // previously dropped with only a console.error, so a battle
          // recorded offline could vanish with no user-visible trace.
          // Surface it the same way the in-band failure path above does.
          this.notifySyncConflict({
            entityId: mutation.entityId,
            matchId: mutation.matchId,
            localState: mutation.payload,
            serverState: undefined,
            resolution: 'SERVER_WINS',
          });
          this.removeMutation(mutation.id);
        }
      }
    }

    // Save updated retry counts
    if (isWalkIn) {
      this.saveWalkInToStorage();
    } else {
      this.saveToStorage();
    }
  }

  /**
   * Check if a walk-in error code represents a resolvable conflict
   * (i.e., the desired state already exists on the server)
   */
  private isWalkInConflictResolvable(errorCode?: string): boolean {
    const resolvableErrors = [
      'PLAYER_ALREADY_REGISTERED',
      'ALREADY_REGISTERED',
      'DUPLICATE_REGISTRATION',
    ];
    return errorCode ? resolvableErrors.includes(errorCode) : false;
  }

  /**
   * Get the appropriate conflict resolution type for a walk-in error
   */
  private getWalkInConflictResolution(errorCode?: string): ConflictType {
    if (errorCode === 'DUPLICATE_PROFILE') {
      return 'DUPLICATE_PROFILE';
    }
    if (
      errorCode === 'PLAYER_ALREADY_REGISTERED' ||
      errorCode === 'ALREADY_REGISTERED' ||
      errorCode === 'DUPLICATE_REGISTRATION'
    ) {
      return 'ALREADY_REGISTERED';
    }
    return 'SERVER_WINS';
  }

  // ===========================================================================
  // Private Methods
  // ===========================================================================

  private setupEventListeners(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  private handleOnline = (): void => {
    this.isOnline = true;
    this.notifyConnectivityChange(true);
  };

  private handleOffline = (): void => {
    this.isOnline = false;
    this.notifyConnectivityChange(false);
  };

  private loadFromStorage(): void {
    if (typeof localStorage === 'undefined') return;

    // Load ref mode mutations
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored) as QueuedMutation[];
        // Filter out stale mutations (older than 24 hours)
        const now = Date.now();
        this.pendingMutations = data
          .filter((mutation) => now - mutation.timestamp < MAX_MUTATION_AGE_MS)
          .map((mutation) => ({
            ...mutation,
            // Migrate legacy matchId to entityId
            entityId: mutation.entityId || mutation.matchId || '',
          }));
        // Save back if we filtered any out
        if (this.pendingMutations.length !== data.length) {
          console.log(
            `Cleaned up ${data.length - this.pendingMutations.length} stale offline mutations`
          );
          this.saveToStorage();
        }
      }
    } catch (error) {
      console.error('Failed to load offline mutations from storage:', error);
      this.pendingMutations = [];
    }

    // Load walk-in mutations
    try {
      const storedWalkIn = localStorage.getItem(WALKIN_STORAGE_KEY);
      if (storedWalkIn) {
        const data = JSON.parse(storedWalkIn) as QueuedMutation[];
        const now = Date.now();
        this.walkInMutations = data.filter(
          (mutation) => now - mutation.timestamp < MAX_MUTATION_AGE_MS
        );
        if (this.walkInMutations.length !== data.length) {
          console.log(
            `Cleaned up ${data.length - this.walkInMutations.length} stale walk-in mutations`
          );
          this.saveWalkInToStorage();
        }
      }
    } catch (error) {
      console.error('Failed to load walk-in mutations from storage:', error);
      this.walkInMutations = [];
    }
  }

  private saveToStorage(): void {
    if (typeof localStorage === 'undefined') return;

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.pendingMutations));
    } catch (error) {
      console.error('Failed to save offline mutations to storage:', error);
    }
  }

  private saveWalkInToStorage(): void {
    if (typeof localStorage === 'undefined') return;

    try {
      localStorage.setItem(WALKIN_STORAGE_KEY, JSON.stringify(this.walkInMutations));
    } catch (error) {
      console.error('Failed to save walk-in mutations to storage:', error);
    }
  }

  private notifyConnectivityChange(isOnline: boolean): void {
    this.connectivityCallbacks.forEach((callback) => {
      try {
        callback(isOnline);
      } catch (error) {
        console.error('Connectivity callback error:', error);
      }
    });
  }

  private notifySyncComplete(): void {
    this.syncCompleteCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error('Sync complete callback error:', error);
      }
    });
  }

  private notifySyncConflict(conflict: SyncConflict): void {
    this.syncConflictCallbacks.forEach((callback) => {
      try {
        callback(conflict);
      } catch (error) {
        console.error('Sync conflict callback error:', error);
      }
    });
  }

  private notifyPendingCountChange(): void {
    const count = this.pendingMutations.length;
    this.pendingCountCallbacks.forEach((callback) => {
      try {
        callback(count);
      } catch (error) {
        console.error('Pending count callback error:', error);
      }
    });
  }

  /**
   * Cleanup event listeners (for testing)
   */
  destroy(): void {
    if (typeof window === 'undefined') return;

    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    this.connectivityCallbacks.clear();
    this.syncCompleteCallbacks.clear();
    this.syncConflictCallbacks.clear();
    this.pendingCountCallbacks.clear();
  }

  /**
   * Reset the manager state (for testing)
   */
  reset(): void {
    this.pendingMutations = [];
    this.walkInMutations = [];
    this.saveToStorage();
    this.saveWalkInToStorage();
    this.notifyPendingCountChange();
  }
}

// =============================================================================
// Helper Functions
// =============================================================================

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// =============================================================================
// Singleton Instance
// =============================================================================

export const offlineManager = new OfflineManager();

// =============================================================================
// Exports
// =============================================================================

export { OfflineManager };
export { WALKIN_STORAGE_KEY, isWalkInMutation };
